import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

type SetValue<T> = Dispatch<SetStateAction<T>>

const useLocalStorage = <T extends string | number | boolean | object | null>(
  key: string,
  initialValue: T,
): [T, SetValue<T>] => {
  const readValue = useCallback((): T => {
    // skip error "window is undefined" when using this hook outside of a browser environment
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch (error) {
      console.info(`Error reading localStorage key “${key}”:`, error)
      return initialValue
    }
  }, [initialValue, key])

  const [storedValue, setStoredValue] = useState<T>(readValue)

  const setValue: SetValue<T> = value => {
    if (typeof window == 'undefined') {
      console.info(
        `Error setting localStorage key “${key}”: window is undefined`,
      )
    }

    try {
      // Allow value to be a function to have the same API as useState
      const newValue = value instanceof Function ? value(storedValue) : value
      window.localStorage.setItem(key, JSON.stringify(newValue))
      setStoredValue(newValue)
    } catch (error) {
      console.info(`Error setting localStorage key “${key}”:`, error)
    }
  }

  useEffect(() => {
    setStoredValue(readValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [storedValue, setValue]
}

export default useLocalStorage
